import { Paper, PaperProps, Stack, Typography, alpha, useTheme } from '@mui/material'
import { BoltRounded } from '@mui/icons-material'

import BatteryLevelBar from '@/components/BatteryLevelBar'

import { MESSAGES } from '@/constants/messages'
import { numericUtils } from '@/utils/numeric'

import { METRIC_LABEL_COLOR, METRIC_MUTED_COLOR, METRIC_VALUE_COLOR } from './metricTokens'

interface ChargingLiveStatusProps extends PaperProps {
  soc?: number | null
  powerActive?: number | null
}

const ChargingLiveStatus: React.FC<ChargingLiveStatusProps> = ({ soc, powerActive, sx, ...props }) => {
  const theme = useTheme()

  const hasSoc = numericUtils.isValidNumericInput(soc)
  const hasPowerActive = numericUtils.isValidNumericInput(powerActive)

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        padding: 1.25,
        borderRadius: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        ...sx,
      }}
      {...props}
    >
      <Stack direction="column" spacing={0.75} width="100%">
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography
            variant="overline"
            fontWeight={700}
            lineHeight={1.2}
            sx={{ color: METRIC_LABEL_COLOR, letterSpacing: '0.12em', fontSize: '0.7rem' }}
          >
            {MESSAGES.CHARGING_SESSION.BATTERY_LABEL}
          </Typography>
          {hasSoc ? (
            <Typography
              variant="h5"
              fontWeight={800}
              lineHeight={1}
              color="primary.main"
              sx={{ fontSize: '1.35rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
            >
              {numericUtils.formatPercentage(soc)}
              <Typography
                component="span"
                variant="body2"
                fontWeight={600}
                sx={{ color: METRIC_MUTED_COLOR, ml: 0.25 }}
              >
                %
              </Typography>
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled" lineHeight={1.2} sx={{ whiteSpace: 'nowrap' }}>
              {MESSAGES.CHARGING_SESSION.BATTERY_UNKNOWN}
            </Typography>
          )}
        </Stack>

        <BatteryLevelBar soc={soc} height={6} />

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <BoltRounded sx={{ fontSize: 16, color: METRIC_MUTED_COLOR }} />
            <Typography variant="caption" lineHeight={1.2} sx={{ color: METRIC_LABEL_COLOR }}>
              {MESSAGES.CHARGING_SESSION.POWER_LABEL}
            </Typography>
          </Stack>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{
              color: hasPowerActive ? METRIC_VALUE_COLOR : 'text.disabled',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {numericUtils.formatKw(powerActive)}
            {hasPowerActive && (
              <Typography
                component="span"
                variant="caption"
                fontWeight={500}
                sx={{ color: METRIC_MUTED_COLOR, ml: 0.5 }}
              >
                kW
              </Typography>
            )}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default ChargingLiveStatus
